// React Hooks:
import React, { useEffect, useState } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  getDirectoryAction,
  directoryListViewAction,
  searchDirectoryAction,
  searchInputAction,
  directory,
} from "./directorySlice";
import {
  authentication,
  logoutAction,
} from "../../../authentication/authenticationSlice";
// MUI Components:
import {
  Container,
  Box,
  Typography,
  Divider,
  SvgIcon,
  Tooltip,
  Collapse,
  Skeleton,
  TextField,
  useTheme,
  Button,
} from "@mui/material";
// Components:
import DirectoryItems from "./directory-items/DirectoryItems";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// MUI SVG Icones:
import SearchIcon from "@mui/icons-material/Search";
// SVG Icones:
import { ReactComponent as FolderHomeIcon } from "../../../../assets/icons/folder-home-icon.svg";
import { ReactComponent as ListIcon } from "../../../../assets/icons/list-icon.svg";
import { ReactComponent as GroupIcon } from "../../../../assets/icons/group-icon.svg";
import { eraseCookie } from "../../../../configs/cookie-helper/cookieHelper";
import { useNavigate } from "react-router-dom";
import { log } from "console";

const useStyles = makeStyles((theme) => ({
  directoryBox: {
    width: "100%",
    height: "95%",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  activeIcon: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
  },
  pathBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      height: "7px",
      width: "7px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  field: {
    borderRadius: "5px",
    backgroundColor: "#DFDFDF",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#DFDFDF",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      "& fieldset": {
        borderColor: "#DFDFDF",
      },
      "&:hover fieldset": {
        borderColor: "#DFDFDF",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#DFDFDF",
        borderWidth: 1.5,
      },
    },
  },
}));

const Directory = () => {
  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { token } = useAppSelector(authentication);

  const { listView, directoryData, searchData, searchInput } =
    useAppSelector(directory);

  const [screenWidth, setScreenWidth] = useState(window.screen.width);

  useEffect(() => {
    if (!!token) {
      dispatch(
        getDirectoryAction({
          path: directoryData.path,
          idfolder: directoryData.idfolder,
        })
      );
    }
  }, [
    dispatch,
    token,
    directoryData.idfolder,
    directoryData.items.length,
    directoryData.path,
  ]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (screenWidth < 600) {
      dispatch(directoryListViewAction(false));
    }
  }, [dispatch, screenWidth]);

  const [authStatus, setAuthStatus] = useState({ message: "", loading: false });

  const navigate = useNavigate();

  useEffect(() => {
    if (directoryData.actionStatus === "loading") {
      setAuthStatus({ message: directoryData.alert.message, loading: true });
    }

    if (!!token && directoryData.actionStatus === "success") {
      setAuthStatus({ message: directoryData.alert.message, loading: false });
    }

    if (directoryData.actionStatus === "rejected") {
      setAuthStatus({ message: directoryData.alert.message, loading: false });
    }
  }, [token, directoryData.actionStatus, directoryData.alert.message]);

  useEffect(() => {    
    if (authStatus.message === "login_again") {
      eraseCookie("token");
      eraseCookie("username");
      eraseCookie("rule");
      navigate("/login");
      dispatch(logoutAction());
    }
  }, [authStatus, navigate, dispatch]);

  const skeletonItems = listView
    ? [1, 2, 3, 4, 5, 6]
    : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];

  return (
    <Container
      maxWidth={false}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        p: { xs: 0 },
      }}
    >
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!!token && searchInput !== "") {
            dispatch(
              searchDirectoryAction({
                path: directoryData.path,
                name: searchInput,
                dir: "true",
              })
            );
          }
        }}
        sx={{
          width: "100%",
          height: "fit-content",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          margin: "0px 0px 8px 0px",
        }}
      >
        <TextField
          name="search"
          placeholder="Search"
          type="text"
          value={searchInput}
          onChange={(e) => {
            dispatch(searchInputAction(e.target.value));
          }}
          size={"small"}
          sx={{
            label: { color: (theme) => theme.palette.text.secondary },
            input: {
              padding: "8px",
              color: (theme) => theme.palette.text.secondary,
              "&::placeholder": {
                color: (theme) => theme.palette.text.secondary,
              },
              "&:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 1000px #DFDFDF inset",
                WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                transition: " background-color 600000s 0s, color 600000s 0s",
              },
              [theme.breakpoints.down("md")]: {
                padding: "6px",
              },
            },
            width: "100%",
          }}
          className={classes.field}
        />
        <Button
          id="ignoreDisableTouch"
          variant="contained"
          color="secondary"
          fullWidth
          type="submit"
          sx={{
            width: "150px",
            height: "39px",
            fontSize: "18px",
            borderRadius: "5px",
            textTransform: "none",
            lineHeight: "0px",
            padding: "12px",
            ml: "8px",
            [theme.breakpoints.down("md")]: {
              width: "140px",
              height: "35px",
              fontSize: "16px",
            },
          }}
        >
          <SvgIcon
            component={SearchIcon}
            inheritViewBox
            sx={{
              width: "auto",
              height: "26px",
              color: "inherit",
              cursor: "pointer",
              mr: "4px",
              [theme.breakpoints.down("md")]: {
                height: "16px",
              },
            }}
          />
          Search
        </Button>
      </Box>

      <Box
        sx={{
          width: "100%",
          height: "fit-content",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            width: "calc(100% - 80px)",
            height: "100%",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <SvgIcon
              component={FolderHomeIcon}
              inheritViewBox
              sx={{
                width: "auto",
                height: "24px",
                color: theme.palette.secondary.light,
                cursor: "pointer",
                "&:hover": {
                  color: (theme) => theme.palette.blue?.main,
                  transition: "all 0.4s",
                },
                [theme.breakpoints.down("md")]: {
                  height: "20px",
                },
              }}
              onClick={() => {
                dispatch(
                  getDirectoryAction({
                    path: "",
                    idfolder: "0",
                  })
                );

                if (searchInput !== "") {
                  dispatch(
                    searchDirectoryAction({
                      path: "",
                      name: searchInput,
                      dir: "true",
                    })
                  );
                }
              }}
            />

            <Typography
              sx={{
                lineHeight: "revert",
                fontSize: "18px",
                color: (theme) => theme.palette.text.secondary,
                mx: "8px",
                [theme.breakpoints.down("md")]: {
                  fontSize: "16px",
                },
              }}
            >
              /
            </Typography>
          </Box>

          <Box
            sx={{ overflowX: "auto", display: "flex", alignItems: "center" }}
            className={classes.pathBox}
          >
            {directoryData.history?.map((item, index) => (
              <Box
                sx={{ display: "flex", alignItems: "flex-start" }}
                key={index}
              >
                <Typography
                  sx={{
                    lineHeight: "revert",
                    fontSize: "18px",
                    color: (theme) => theme.palette.text.secondary,
                    cursor: "pointer",
                    "&:hover": {
                      color: (theme) => theme.palette.blue?.main,
                      transition: "all 0.4s",
                    },
                    [theme.breakpoints.down("md")]: {
                      fontSize: "16px",
                    },
                  }}
                  onClick={() => {
                    dispatch(
                      getDirectoryAction({
                        path: item.path,
                        idfolder: item.id,
                      })
                    );

                    if (searchInput !== "") {
                      dispatch(
                        searchDirectoryAction({
                          path: item.path
                            .split("/")
                            .filter((item: string, index: number) => index > 0)
                            .join("/"),
                          name: searchInput,
                          dir: "true",
                        })
                      );
                    }
                  }}
                >
                  {item.name}
                </Typography>

                <Typography
                  sx={{
                    lineHeight: "revert",
                    fontSize: "18px",
                    color: (theme) => theme.palette.text.secondary,
                    mx: "8px",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "16px",
                    },
                  }}
                >
                  /
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          id="ignoreDisableTouch"
          sx={{
            minWidth: "fit-content",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "width 5s",
            [theme.breakpoints.down("sm")]: {
              display: "none",
            },
          }}
        >
          <Tooltip
            arrow
            placement="bottom"
            title="List"
            enterDelay={1000}
            enterNextDelay={1000}
            leaveDelay={500}
            componentsProps={{
              tooltip: {
                sx: {
                  marginTop: "10px !important",
                },
              },
            }}
          >
            <Box
              sx={{
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "all 0.2s",
                cursor: "pointer",
                mr: "8px",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.paper,
                  filter: `${listView ? "" : "brightness(85%)"}`,
                },
                [theme.breakpoints.down("md")]: {
                  width: "26px",
                  height: "26px",
                },
              }}
              className={listView ? classes.activeIcon : ""}
              onClick={() => dispatch(directoryListViewAction(true))}
            >
              <SvgIcon
                component={ListIcon}
                inheritViewBox
                sx={{
                  width: "20px",
                  height: "20px",
                  color: "inherit",
                  stroke: (theme) => theme.palette.text.secondary,
                  opacity: "0.5",
                  cursor: "pointer",
                  [theme.breakpoints.down("md")]: {
                    width: "14px",
                    height: "14px",
                    opacity: "0.7",
                  },
                }}
              />
            </Box>
          </Tooltip>

          <Tooltip
            arrow
            placement="bottom"
            title="Large icones"
            enterDelay={1000}
            enterNextDelay={1000}
            leaveDelay={500}
            componentsProps={{
              tooltip: {
                sx: {
                  marginTop: "10px !important",
                },
              },
            }}
          >
            <Box
              sx={{
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "all 0.2s",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.paper,
                  filter: `${listView ? "brightness(85%)" : ""}`,
                },
                [theme.breakpoints.down("md")]: {
                  width: "26px",
                  height: "26px",
                },
              }}
              className={listView ? "" : classes.activeIcon}
              onClick={() => dispatch(directoryListViewAction(false))}
            >
              <SvgIcon
                component={GroupIcon}
                inheritViewBox
                sx={{
                  width: "20px",
                  height: "20px",
                  color: "inherit",
                  stroke: (theme) => theme.palette.text.secondary,
                  opacity: "0.5",
                  cursor: "pointer",
                  [theme.breakpoints.down("md")]: {
                    width: "14px",
                    height: "14px",
                    opacity: "0.7",
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Divider
        sx={{
          margin: "12px 0px 12px 0px",
          [theme.breakpoints.down("md")]: {
            margin: "8px 0px 8px 0px",
          },
        }}
      />

      <Box
        sx={{
          width: "100%",
          height: listView ? "calc(100% - 179px)" : "calc(100% - 104px)",
          [theme.breakpoints.down("md")]: {
            height: listView ? "calc(100% - 141px)" : "calc(100% - 86px)",
          },
          [theme.breakpoints.down("sm")]: {
            height: "calc(100% - 84px)",
          },
        }}
      >
        <Collapse in={listView} timeout={400}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexWrap: "no-wrap",
              flexDirection: "column",
              alignContent: "flex-start",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              padding: "0px 8px 0px 8px",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "75px",
                minHeight: "75px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "16px",
                borderRadius: "8px",
                backgroundColor: (theme) => theme.palette.secondary.main,
                color: (theme) => theme.palette.secondary.contrastText,
                [theme.breakpoints.down("md")]: {
                  height: "55px",
                  minHeight: "55px",
                },
              }}
            >
              <Box
                sx={{
                  width: "55px",
                  height: "100%",
                  [theme.breakpoints.down("md")]: {
                    width: "40px",
                  },
                }}
              ></Box>
              <Box
                sx={{
                  width: "calc(100% - 55px)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  [theme.breakpoints.down("md")]: {
                    width: "calc(100% - 40px)",
                  },
                }}
              >
                <Typography
                  sx={{
                    width: "35%",
                    height: "24px",
                    textAlign: "left",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontSize: "16px",
                    pl: "8px",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "12px",
                      height: "16px",
                    },
                  }}
                >
                  Name
                </Typography>

                <Typography
                  sx={{
                    width: "30%",
                    height: "24px",
                    textAlign: "left",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontSize: "16px",
                    pl: "8px",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "12px",
                      height: "16px",
                    },
                  }}
                >
                  Date modified
                </Typography>

                <Typography
                  sx={{
                    width: "20%",
                    height: "24px",
                    textAlign: "left",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontSize: "16px",
                    pl: "8px",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "12px",
                      height: "16px",
                    },
                  }}
                >
                  Type
                </Typography>

                <Typography
                  sx={{
                    width: "15%",
                    height: "24px",
                    textAlign: "left",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontSize: "16px",
                    pl: "8px",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "12px",
                      height: "16px",
                    },
                  }}
                >
                  Size
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Box
          sx={{
            width: "100%",
            height: "calc(100%)",
            display: "flex",
            flexWrap: `${listView ? "no-wrap" : "wrap"}`,
            flexDirection: `${listView ? "column" : "row"}`,
            alignContent: directoryData.alert.error ? "center" : "flex-start",
            alignItems: directoryData.alert.error ? "center" : "flex-start",
            justifyContent: directoryData.alert.error ? "center" : "flex-start",
            padding: "0px 8px 8px 8px",
            [theme.breakpoints.down("sm")]: {
              width: "calc(100% + 8px)",
            },
          }}
          className={classes.directoryBox}
        >
          {directoryData.actionStatus === "loading" ||
          searchData.actionStatus === "loading" ? (
            skeletonItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  width: `${listView ? "100%" : "120px"}`,
                  height: `${listView ? "75px" : "130px"}`,
                  minHeight: `${listView ? "75px" : "fit-content"}`,
                  padding: "4px",
                  margin: `${listView ? "4px 0px 4px 0px" : "4px 4px 4px 4px"}`,
                  display: "flex",
                  flexDirection: `${listView ? "row" : "column"}`,
                  alignItems: "center",
                  justifyContent: `${
                    listView ? "flex-start" : "space-between"
                  }`,
                  transition:
                    "height 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)",
                  [theme.breakpoints.down("md")]: {
                    width: `${listView ? "100%" : "95px"}`,
                    height: `${listView ? "55px" : "105px"}`,
                    minHeight: `${listView ? "55px" : "fit-content"}`,
                  },
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={"100%"}
                  height={"100%"}
                  animation="wave"
                  sx={{ bgcolor: "#e8e8e8" }}
                />
              </Box>
            ))
          ) : directoryData.alert.error ? (
            <Typography
              variant="h4"
              sx={{
                overflow: "hidden",
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              {directoryData.alert.message}
            </Typography>
          ) : (
            <DirectoryItems />
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Directory;
